import React from 'react';
import { Play, Pause, Pencil, ExternalLink, Trash2, Clock, Zap } from 'lucide-react';
import { Button } from './Button';
import { Badge } from './Badge';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from './Tooltip';
import type { Workflow } from '../helpers/useWorkflowsApi';
import styles from './WorkflowItem.module.css';

interface WorkflowItemProps {
  workflow: Workflow;
  onRun?: (workflow: Workflow) => void;
  onEdit?: (workflow: Workflow) => void;
  onDelete?: (workflow: Workflow) => void;
  onClick?: (workflow: Workflow) => void;
  isRunning?: boolean;
  isSelected?: boolean;
}

export const WorkflowItem: React.FC<WorkflowItemProps> = ({
  workflow,
  onRun,
  onEdit,
  onDelete,
  onClick,
  isRunning = false,
  isSelected = false,
}) => {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <TooltipProvider>
      <div
        className={`${styles.item} ${workflow.active ? styles.active : ''} ${isSelected ? styles.selected : ''}`}
        onClick={() => onClick?.(workflow)}
        style={{ cursor: onClick ? 'pointer' : undefined }}
      >
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <Zap className={styles.icon} size={16} />
            <h3 className={styles.title}>{workflow.name}</h3>
            <Badge
              variant={workflow.active ? 'default' : 'secondary'}
              className={styles.statusBadge}
            >
              {workflow.active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
          {workflow.description && <p className={styles.description}>{workflow.description}</p>}
        </div>

        <div className={styles.meta}>
          <div className={styles.metaItem}>
            <Clock size={12} />
            <span>Updated {formatDate(workflow.updatedAt)}</span>
          </div>
          {workflow.tags.length > 0 && (
            <div className={styles.tags}>
              {workflow.tags.slice(0, 3).map((tag) => (
                <Badge key={tag} variant="outline" className={styles.tag}>
                  {tag}
                </Badge>
              ))}
              {workflow.tags.length > 3 && (
                <span className={styles.moreTags}>+{workflow.tags.length - 3}</span>
              )}
            </div>
          )}
        </div>

        <div className={styles.actions}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon-sm"
                variant="ghost"
                onClick={() => onRun?.(workflow)}
                disabled={isRunning}
                className={styles.actionButton}
              >
                {isRunning ? <Pause size={16} /> : <Play size={16} />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Run workflow</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon-sm"
                variant="ghost"
                onClick={() => onEdit?.(workflow)}
                className={styles.actionButton}
              >
                <Pencil size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Edit workflow</TooltipContent>
          </Tooltip>

          {workflow.n8nUrl && (
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={workflow.n8nUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.externalLink}
                >
                  <Button size="icon-sm" variant="ghost" className={styles.actionButton}>
                    <ExternalLink size={16} />
                  </Button>
                </a>
              </TooltipTrigger>
              <TooltipContent>Open in n8n</TooltipContent>
            </Tooltip>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon-sm"
                variant="ghost"
                onClick={() => onDelete?.(workflow)}
                className={`${styles.actionButton} ${styles.deleteButton}`}
              >
                <Trash2 size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Delete workflow</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  );
};
